'use client';
import ReviewsSocial from '@/components/Reviews/ReviewsSocial';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import Image from 'next/image';
import { ReactNode } from 'react';
import CustomRichText from '../CustomRichText';

const defaultStats = [
  { id: 1, name: 'років досвіду', value: '5' },
  { id: 2, name: 'тис. студентів', value: '5+' },
  { id: 3, name: 'рейтинг відгуків Google', value: '5' },
  { id: 4, name: 'год і 41с рекорд України', value: '96' },
];

const defaultImages = [
  {
    image: 'https://academy.ap.education/assets/video/reviewsgoogle.mp4',
    url: 'https://www.google.com/search?q=AP+English+Education+Center+%D0%B2%D1%96%D0%B4%D0%B3%D1%83%D0%BA%D0%B8&sca_esv=7a56bf203bd23989&sca_upv=1&sxsrf=ADLYWIIOaxyYKRN4cB2jm9hQ3HBzI20NQQ%3A1725537223225&ei=x5vZZuS6DY_lxc8PvOmg6A8&oq=ap+education+%D0%B2%D1%96%D0%B4%D0%B3%D1%83%D0%BA%D0%B8&gs_lp=Egxnd3Mtd2l6LXNlcnAiG2FwIGVkdWNhdGlvbiDQstGW0LTQs9GD0LrQuCoCCAEyChAAGLADGNYEGEcyChAAGLADGNYEGEcyChAAGLADGNYEGEcyChAAGLADGNYEGEcyChAAGLADGNYEGEdIsCFQAFgAcAF4AZABAJgBAKABAKoBALgBAcgBAJgCAaACB5gDAIgGAZAGBZIHATGgBwA&sclient=gws-wiz-serp#lrd=0x473add6e33d20fbf:0x55301809321ab0bc,1,,,,',
  },
  {
    image: 'https://academy.ap.education/assets/video/reviewsfb.mp4',
    url: 'https://www.facebook.com/ap.edu.centre/reviews',
  },
  {
    image: 'https://academy.ap.education/assets/video/reviews.mp4',
    url: 'https://top20.ua/lviv/navchannya-kursi/kursi-inozemnih-mov/ap-english-education-center.html',
  },
];
export interface ReviewsBlockCustomProps {
  color?: 'violet' | 'green' | 'blue';
  id?: string;
  title?: ReactNode | SerializedEditorState;
  subtitle?: ReactNode | SerializedEditorState;
  thankYouText?: ReactNode | SerializedEditorState;
  stats?: Array<{
    id: number;
    name: string;
    value: string;
  }>;
  images?: Array<{
    image: string | { url: string; alt: string };
    url: string;
  }>;
}

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(' ');
}

export default function ReviewsBlockCustom({
  color = 'green',
  id = 'reviews-block',
  title = 'Ваші відгуки роблять нас кращими',
  subtitle = 'Кожне ваше повідомлення — це можливість стати краще. Ми цінуємо вашу довіру та завжди готові допомогти!',
  thankYouText = 'Дякуємо за вашу довіру!',
  stats = defaultStats,
  images = defaultImages,
}: ReviewsBlockCustomProps) {
  const backgroundColorClassCircle =
    {
      green: 'blur-bg-gradient-green',
      blue: 'blur-bg-gradient-blue',
      violet: 'blur-bg-gradient-violet',
    }[color] || 'custom-gradient-green';

  const backgroundColorClass =
    {
      green: 'bg-green-500/25',
      blue: 'bg-blue-500/25',
      violet: 'bg-violet-500/25',
    }[color] || 'bg-green-500/25';

  return (
    <div id={id} data-meta="ReviewsBlockCustom" className="container mx-auto lg:px-8 my-24">
      <div className="mx-auto">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          <div
            className={` mb-8 px-4 flex justify-start lg:col-span-7  lg:mt-0 lg:mb-0  rounded-3xl`}
          >
            <div
              className={`relative flex flex-col p-4  ${backgroundColorClassCircle} justify-between mx-auto md:py-8 lg:mx-0`}
            >
              <div
                className={classNames(
                  color === 'blue' && 'bg-blue-500/25',
                  color === 'violet' && 'bg-violet-500/25',
                  color === 'green' && 'bg-green-500/25',
                  `absolute top-0 left-0 right-0 bottom-0 -z-50 rounded-3xl`,
                )}
              ></div>
              <div className="max-w-xl text-5xl font-bold tracking-tight text-white">
                <CustomRichText data={title} tag="span" />
                <div className="float-right">
                  <div className="relative max-w-fit rounded-full text-xl px-4 py-1 leading-7 font-fixel text-white backdrop-blur-sm bg-white/30 -rotate-3">
                    <CustomRichText data={thankYouText} tag="span" />
                    <div className="absolute -top-[50%] -right-[5%]">
                      <Image
                        src={'/assets/icon/emoji/small-stars.svg'}
                        width={32}
                        height={32}
                        alt={''}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="">
                <CustomRichText data={subtitle} tag="div" className="my-6 text-md text-white" />
                <div className="relative">
                  <div aria-hidden="true" className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                </div>
                <div className="">
                  <div className="mx-auto pt-4">
                    <div className="grid grid-cols-1 max-lg:gap-x-1 gap-y-2 lg:flex gap-4 justify-between">
                      {stats.map(stat => (
                        <div
                          key={`${stat.id}-${stat.name}`}
                          className="lg:mx-auto flex justify-start lg:justify-between items-center gap-4"
                        >
                          <p className="text-xs text-white">{stat.name}</p>
                          <p className="order-first text-4xl font-semibold tracking-tight text-white">
                            {stat.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ReviewsSocial images={images} color={color} />
        </div>
      </div>
    </div>
  );
}
